import {
  GetApp,
  PowerSettingsNew,
  SettingsInputComponent,
} from '@material-ui/icons';
import React, { useEffect, useRef, useState } from 'react';
import * as R from 'ramda';
import {
  InstallerStatus,
  MSTransitionalStatus,
} from '@pos-refacto/installer-rxjs';
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Popover,
  Table,
  Typography,
} from '@material-ui/core';
import { useSet } from 'react-use';

import { intersectEquals } from 'utils';
import {
  UrgentAlert,
  useUnhideUrgentAlert,
} from 'containers/Header/components/Installer/UrgentAlert';

import { useMSRequirements } from './useMSRequirements';
import { useAutoInstallMS } from './useAutoInstallMS';
import useSaveMSEndpointsToLS from './useSaveMSEndpointsToLS';
import {
  useRegisterDevice
} from 'containers/Header/components/Installer/useRegisterDevice';

const P = ({ children }) => (
  <Typography component="span" color="primary">
    {children}
  </Typography>
);

const statusCheck = (onStatuses: any[], workingStatuses: any[]) => (
  status,
  expectedStatus,
) => {
  return {
    on: onStatuses.includes(expectedStatus ?? status),
    working: workingStatuses.includes(expectedStatus ?? status),
  };
};

const installed = statusCheck(
  [
    MSTransitionalStatus.STARTING,
    MSTransitionalStatus.STOPPING,
    MSTransitionalStatus.UPDATING_CONFIG,
    MSTransitionalStatus.REMOVING,
    'StatusRunning',
    'StatusStopped',
  ],
  [
    MSTransitionalStatus.DOWNLOADING,
    MSTransitionalStatus.INSTALLING,
    MSTransitionalStatus.REMOVING,
  ],
);
const running = statusCheck(
  ['StatusRunning', MSTransitionalStatus.STOPPING],
  [MSTransitionalStatus.STARTING, MSTransitionalStatus.STOPPING],
);

const StatusLight = ({
  presumed = undefined,
  status = '',
  color = 'lime',
}: {
  presumed:
    | MSTransitionalStatus
    | 'StatusRunning'
    | 'StatusStopped'
    | undefined;
  status: '' | 'StatusRunning' | 'StatusStopped';
  color?: string;
}) => {
  const stylesFor = ({ on, working }) => {
    if (on && working)
      return {
        color,
        filter: 'drop-shadow(0 0 4px white) saturate(0.5) brightness(2)',
      };
    if (on) return { color };
    if (working)
      return {
        color: 'gray',
        filter: 'drop-shadow(0 0 4px white) saturate(0.5) brightness(2)',
      };
    return { color: 'gray' };
  };
  return (
    <Box padding={1} style={{ background: 'black', color: 'white' }}>
      <GetApp style={stylesFor(installed(status, presumed))} />
      <PowerSettingsNew style={stylesFor(running(status, presumed))} />
    </Box>
  );
};
const InstallerStatusPanel = ({
  status,
  actualConfigs = {},
  expectedConfigs = {},
}: {
  status: InstallerStatus | Error | undefined;
  actualConfigs: object;
  expectedConfigs: object;
}) => {
  const [expanded, { toggle }] = useSet(new Set());
  const msRequirements = useMSRequirements(status);

  if (!status) return <CircularProgress variant="indeterminate" />;
  if (status instanceof Error)
    return <Typography color="error">{status.message}</Typography>;

  return (
    <Box margin={1} width={500}>
      <Box>
        {status.presumedStatus && <LinearProgress variant="indeterminate" />}
        <Box>
          HTTP:{' '}
          <P>
            {status.httpCode} {status.presumedStatus ?? status.status}
          </P>
        </Box>
        <Box>
          Time:{' '}
          <P>
            {status.dateTime.replace(
              /(....)(..)(..)(..)(..)(..)/,
              '$1-$2-$3 $4:$5:$6',
            )}
          </P>
        </Box>
        <Box>
          Message: <P>{status.message}</P>
        </Box>
        <Box>
          Version: <P>{status.data.version}</P>
        </Box>
        <Box>
          Description: <P>{status.data.description}</P>
        </Box>
        <Box>
          Config:{' '}
          <P>
            {JSON.stringify(
              JSON.parse(status.data.config ?? '{}'),
              undefined,
              4,
            )}
          </P>
        </Box>
      </Box>
      <Box>
        <Table>
          <tbody>
            <tr>
              <th colSpan={3}>Integrations required</th>
            </tr>
            <tr>
              <td>Name</td>
              <td>Version</td>
              <td>Status</td>
            </tr>
            {Object.keys(expectedConfigs)
              .sort((a, b) => {
                if (a < b) return -1;
                if (a > b) return 1;
                return 0;
              })
              .map(k => {
                const int = status.data.integrations.find(i => i.name === k);
                return (
                  <React.Fragment key={`${k}-1`}>
                    <tr>
                      <td onClick={() => toggle(k)}>
                        <P>{k}</P>
                      </td>
                      <td>
                        <P>{int?.version}</P>
                      </td>
                      <td>
                        <Box display="flex" flexDirection="column">
                          <StatusLight
                            presumed={int?.presumedStatus}
                            status={int?.status ?? ''}
                            color={
                              intersectEquals(
                                expectedConfigs[k].config,
                                actualConfigs[k],
                              )
                                ? 'lime'
                                : 'orange'
                            }
                          />
                        </Box>
                      </td>
                    </tr>
                    {expanded.has(k) && (
                      <tr>
                        <td colSpan={3}>
                          <Box margin={2}>
                            <Table>
                              <tbody>
                                <tr>
                                  <td>Key</td>
                                  <td>Actual value</td>
                                  <td>Expected value</td>
                                </tr>
                                {Array.from(
                                  new Set(
                                    Object.keys({
                                      ...actualConfigs[k],
                                      ...expectedConfigs[k].config,
                                    }),
                                  ),
                                ).map((key: string) => {
                                  const [a, b] = [
                                    actualConfigs[k][key],
                                    expectedConfigs[k].config[key],
                                  ];
                                  const managed = ![a, b].includes(undefined);

                                  const color = R.when<any, string | undefined>(
                                    () => managed,
                                    () => (R.equals(a, b) ? 'green' : 'red'),
                                  )(undefined);

                                  return (
                                    <tr
                                      key={k}
                                      style={{
                                        opacity: !managed ? 0.5 : undefined,
                                      }}
                                    >
                                      <td>{key}</td>
                                      <td style={{ color }}>
                                        {JSON.stringify(a)}
                                      </td>
                                      <td>{JSON.stringify(b)}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </Box>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

            <tr>
              <th colSpan={3}>Other installed integrations</th>
            </tr>
            {status.data.integrations
              .filter(({ name }) => !msRequirements[name])
              .map(int => (
                <React.Fragment key={`${int.name}-1`}>
                  <tr>
                    <td onClick={() => toggle(int.name)}>
                      <P>{int.name}</P>
                    </td>
                    <td>
                      <P>{int.version}</P>
                    </td>
                    <td>
                      <Box display="flex" flexDirection="column">
                        <StatusLight
                          presumed={int.presumedStatus}
                          status={int.status}
                          color="orange"
                        />
                      </Box>
                    </td>
                  </tr>
                  {expanded.has(int.name) && (
                    <tr>
                      <td colSpan={3}>
                        <Box margin={2}>
                          <Table>
                            <tr>
                              <td>Key</td>
                              <td>Value</td>
                            </tr>
                            {Object.keys(actualConfigs[int.name]).map(
                              (key: string) => {
                                const a = actualConfigs[int.name][key];

                                return (
                                  <tr key={int.name} style={{ opacity: 0.5 }}>
                                    <td>{key}</td>
                                    <td>{JSON.stringify(a)}</td>
                                  </tr>
                                );
                              },
                            )}
                          </Table>
                        </Box>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
          </tbody>
        </Table>
      </Box>
    </Box>
  );
};

export const HeaderInstaller = () => {
  const {
    info: { installerStatus, actualConfigs, expectedConfigs },
    errors,
    processing: autoInstalling,
    retry,
  } = useAutoInstallMS();
  const someMsAreRequired = !!Object.values(expectedConfigs).length;

  useSaveMSEndpointsToLS();

  useRegisterDevice();

  const color = (() => {
    if (autoInstalling) return 'yellow';

    const criticalIssue =
      errors.status ||
      errors.autoInstall ||
      errors.msCert ||
      errors.version ||
      installerStatus instanceof Error ||
      installerStatus?.httpCode !== 200;
    const configIssue = errors.config;
    const loading = installerStatus === undefined;

    if (someMsAreRequired) {
      if (criticalIssue) return 'red';
      if (configIssue) return 'orange';
      if (loading) return 'orange';
    } else {
      if (criticalIssue) return 'gray';
      if (configIssue) return 'gray';
      if (loading) return 'gray';
    }

    return 'lime';
  })();

  const unhideUrgentAlert = useUnhideUrgentAlert();
  const [open, setOpen] = useState<HTMLSpanElement | null>(null);
  return (
    <>
      <UrgentAlert isRelevant={installerStatus instanceof Error && someMsAreRequired} />
      <span
        style={{ color }}
        onClick={e => {
          setOpen(e.currentTarget);
          unhideUrgentAlert();
        }}
        id="header-installer-toggle"
      >
        <SettingsInputComponent />
      </span>
      <Popover
        open={!!open}
        anchorEl={open}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        className="erply-header__dropdown erply-header__dropdown--installer"
        data-testid="installer"
        onClose={() => setOpen(null)}
      >
        {installerStatus && (
          <InstallerStatusPanel
            status={installerStatus}
            actualConfigs={actualConfigs}
            expectedConfigs={expectedConfigs}
          />
        )}

        <hr />
        {autoInstalling ? (
          <Typography style={{ color: 'gold' }}>
            Auto-install in progress...
          </Typography>
        ) : null}

        {errors.status ? (
          <Typography style={{ color: 'red' }}>
            Some required MS are not running
          </Typography>
        ) : null}
        {errors.autoInstall ? (
          <Typography style={{ color: 'red' }}>
            Automatic installation has failed:{' '}
            {(errors.autoInstall as Error)?.message}
          </Typography>
        ) : null}
        {errors.config ? (
          <Typography style={{ color: 'orange' }}>
            Some MS has incorrect configuration
          </Typography>
        ) : null}
        {errors.version ? (
          <Typography style={{ color: 'red' }}>
            Some MS has incorrect version and will be updated
          </Typography>
        ) : null}
        {errors.msCert ? (
          <Typography style={{ color: 'red' }}>
            Some MS is inacessible and will be updated
          </Typography>
        ) : null}
        {installerStatus instanceof Error ? (
          <Typography style={{ color: 'red' }}>
            Auto-install failed with error: {installerStatus.message}
          </Typography>
        ) : null}
        {!(installerStatus instanceof Error) &&
        installerStatus?.httpCode !== 200 ? (
          <Typography style={{ color: 'red' }}>
            Failed to connect to installer: {installerStatus?.message}
          </Typography>
        ) : null}

        {retry && (
          <Button
            onClick={retry}
            color="secondary"
            variant="contained"
            disabled={autoInstalling}
          >
            Rerun auto-install
          </Button>
        )}
      </Popover>
    </>
  );
};
